/** @jsxImportSource @emotion/react */
import { cx } from '@emotion/css';
import type { Theme } from '@emotion/react';
import type { FlipProp, RotateProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import type { To } from 'react-router-dom';
import camelcase from 'camelcase';
import type { ButtonHTMLAttributes, ReactNode, ElementType } from 'react';
import { Children, forwardRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import type { ClassificationColors, ColorPalette, ThemeColors } from '../../utils';
import {
  colorContrast,
  darkenBy,
  getClassificationColor,
  getColorPalette,
  getThemeColor,
} from '../../utils';
import type { IconName } from '../Icon';
import Icon from '../Icon';
import ODSBadge from '../ODSBadge';
import ODSInfo from '../ODSInfo';
import ODSTooltip from '../ODSTooltip';

const messages = defineMessages({
  beta: {
    id: 'ODSButton.beta',
    defaultMessage: 'Experimental feature',
  },
});

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Don't want the btn to appear inline, make it a block */
  block?: boolean;
  /** Need a close btn, displays a button with just the _times_ icon */
  close?: boolean;
  /** Do you need a bigger button? */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Is only 1 button the user need to press, make it a call to action button */
  cta?: boolean;
  /** Make it a quiet button, without a background or border */
  quiet?: boolean;
  /** Make it an outline button */
  outline?: boolean;
  /** Use when the button is in a toolbar */
  toolbar?: boolean;
  /** For use when displaying a grid of options and only 1 is selectable in that group */
  toggle?: boolean;
  /** Use this when the button says Delete or something dangerous */
  danger?: boolean;
  /** When it's not quite dangerous but not quite safe */
  warning?: boolean;
  /** Use to get a little more contrast (black or white) */
  highContrast?: boolean;
  /** When on a dark color use this */
  onDark?: boolean;
  /** You want the button to go the full distance */
  fullWidth?: boolean;
  /** Pass in an icon to the button */
  icon?: IconName;
  /** Need the icon to change on hover? */
  hoverIcon?: IconName;
  /** Tell the user this button is toggled on */
  active?: boolean;
  /** Does this button take you somewhere? Convert button into a hyperlink */
  link?: To;
  /** Do you want this to not be a button? Pass in a div or Link */
  tag?: ElementType;
  /** Tooltips are helpful, pass in some text to help the user know what this does */
  tooltip?: ReactNode;
  /** Want an arrow on the tooltip? */
  tooltipArrow?: boolean;
  /** If you don't want the tooltip to try and open up */
  tooltipPlacement?: 'left' | 'right' | 'top' | 'bottom';
  /** Need the tooltip farther away? */
  tooltipOffset?: number;
  /** Pass in a keyboard shortcut to be displayed in the tooltip */
  shortcut?: ReactNode;
  /** Is this button also a dropdown, pass this prop in */
  hasCaret?: boolean;
  /** Need to rotate that icon? Pass in the degrees */
  iconRotate?: RotateProp;
  /** Flip the icon horizontally, vertically, or both. */
  iconFlip?: FlipProp;
  /** Is this special, give the icon a color */
  iconColor?: string | null;
  /** Need the icon smaller or bigger, here you go */
  iconSize?: SizeProp;
  /** Pass in a custom icon or image */
  customIcon?: ReactNode;
  /** Only use this if you know why you need to. Pass in the bootstrap color name: aqua, pink, midnight */
  color?: string;
  /** For DropdownToggle buttons, ignore */
  dropdownColor?: string;
  /** If link is true this can be set to true which will add target="_blank" to the link element */
  openLinkInNewTab?: boolean;
  /** Want text to be cut off? */
  truncate?: boolean;
  /** For rare cases, use with caution */
  tiny?: boolean;
  /** When you don't want to color an icon, but want to show a preview of the color for something like text color */
  swatch?: string;
  /** Display experimental feature badge */
  beta?: boolean;
  /** Use when the button activates another feature for empty states and user onboarding */
  help?: boolean;
  /** @deprecated This prop is poorly implemented, coupled to other `minWidth` functionality, and rarely used.
   * Intention was to disable buttons left and right side padding */
  padding?: boolean;
  /** Change button text alignment, when caret isn't present */
  alignText?: 'start' | 'center' | 'end';
  /** Spin the icon */
  iconSpin?: boolean;
  /** Adds a badge inside the button, good for counts and such */
  badge?: ReactNode;
  /** Override the default color for the badge */
  badgeColor?: ThemeColors | ColorPalette | ClassificationColors | string;
  /** Test id for the badge */
  badgeTestId?: string;
  /** Accessible name for the button */
  ariaLabel?: string;
  /** Pass in the background color the button will be on to determine button color */
  determineColor?: ThemeColors | ColorPalette | ClassificationColors | string;
  /** Test id for the icon */
  iconTestId?: string;
  /** Pass in a testid */
  testSelector?: string;
  /** Pass in a filename if a link and downloading a file */
  download?: string;
  /** Pass in a target for the link */
  target?: '_blank' | '_self' | '_parent' | '_top' | string;
  /** Use this in conjunction with tag and pass in a react-dom-router Link: tag={Link} */
  to?: To;
}

const ODSButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      children,
      block,
      icon,
      close,
      size = 'sm',
      outline,
      cta,
      quiet,
      danger,
      warning,
      highContrast,
      toolbar,
      toggle,
      disabled,
      onDark,
      fullWidth,
      active,
      hoverIcon,
      link,
      tag: Tag = link ? 'a' : 'button',
      tooltip,
      tooltipArrow,
      tooltipPlacement = 'top',
      tooltipOffset = 4,
      shortcut,
      hasCaret,
      iconRotate,
      iconFlip,
      iconColor,
      iconSize,
      color,
      dropdownColor,
      truncate,
      tiny,
      type = 'button',
      swatch,
      beta,
      customIcon,
      help,
      openLinkInNewTab = false,
      padding = true,
      alignText = 'center',
      iconSpin = false,
      badge,
      badgeColor,
      badgeTestId,
      ariaLabel,
      determineColor,
      iconTestId,
      testSelector,
      target,
      download,
      to,
      ...componentProps
    },
    ref
  ) => {
    const [isHovering, setHovering] = useState(false);
    const hasChildren = Children.toArray(children)?.length !== 0 && children !== '';

    let btnColor = !onDark ? 'secondary' : 'light';
    let isRawColor = false;

    if (color) {
      const camelColor = camelcase(color);
      isRawColor =
        !getThemeColor(camelColor) &&
        !getColorPalette(camelColor) &&
        !getClassificationColor(camelColor);
      btnColor = isRawColor ? 'secondary' : color;
    } else if (dropdownColor) {
      btnColor = dropdownColor;
    } else if (highContrast) {
      btnColor = !onDark ? 'black' : 'white';
    } else if (danger) {
      btnColor = 'danger';
    } else if (warning) {
      btnColor = 'warning';
    } else if (cta) {
      btnColor = 'primary';
    } else if (determineColor) {
      const actualColor =
        getThemeColor(determineColor) ??
        getClassificationColor(determineColor) ??
        getColorPalette(determineColor) ??
        determineColor;
      const onLight = colorContrast(actualColor, 'white') < 4.5;
      btnColor = onLight ? 'black' : 'white';
    }

    if (toolbar) {
      btnColor += '-toolbar';
    } else if (toggle) {
      btnColor += '-toggle';
    } else if (quiet && !outline) {
      btnColor += '-quiet';
    } else if (help) {
      btnColor = 'primary-quiet';
    }

    let btnIcon = icon;
    if (close && !btnIcon) {
      btnIcon = 'times';
    }

    const hasContent = !!hasChildren || !!btnIcon || !!customIcon || !!badge;

    const btnContent = (
      <>
        {!!btnIcon && (
          <div className="flex-shrink-0">
            <Icon
              name={!hoverIcon || !isHovering ? btnIcon : hoverIcon}
              size={iconSize}
              fixedWidth
              {...(!!iconRotate && { rotate: iconRotate })}
              {...(!!iconFlip && { flip: iconFlip })}
              {...(!!iconColor && { color: iconColor })}
              style={{ display: 'block' }}
              className="btn-icon"
              spin={iconSpin}
              testId={iconTestId}
            />
          </div>
        )}
        {!!customIcon && <div className="flex-shrink-0 btn-icon">{customIcon}</div>}
        {hasChildren && (
          <div
            className={cx('btn-text', { 'text-truncate': truncate })}
            css={{ '&:empty': { display: 'none' } }}
          >
            {children}
          </div>
        )}
        {!!badge && (
          <ODSBadge
            color={badgeColor ?? color}
            size="xs"
            data-testid={badgeTestId}
            className="ms-auto"
          >
            {badge}
          </ODSBadge>
        )}
      </>
    );

    const btn = (
      <Tag
        aria-label={ariaLabel}
        data-testid={testSelector}
        className={cx(
          'btn align-items-center',
          `btn-${size}`,
          `btn${outline && !toolbar ? '-outline' : ''}-${btnColor}`,
          className,
          hasCaret && hasContent
            ? 'justify-content-between gap-2'
            : `justify-content-${alignText} gap-1`,
          {
            'rounded-pill': tiny,
            active,
            disabled,
            'd-flex': block,
            'position-relative': beta,
            'flex-shrink-0': !truncate && !fullWidth,
            'w-100': fullWidth,
            'fw-bold': help,
          }
        )}
        disabled={disabled}
        {...(!tooltip && { ref })}
        {...(Tag === 'button' && { type })}
        css={(theme: Theme) => ({
          whiteSpace: 'nowrap',
          position: 'relative',
          ...(((!hasChildren && !hasCaret) || !padding) && {
            minWidth: !tiny ? '1.75rem' : '1.125rem',
          }),
          ...(!padding && {
            padding: 0,
          }),
          ...(!hasContent &&
            hasCaret && {
              minWidth: '1.25rem',
            }),
          ...(tiny && { minHeight: '1.125rem', fontSize: '0.75rem' }),
          ...(size === 'xs' && {
            minHeight: '1.25rem',
            minWidth: '1.25rem',
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
          }),
          '*': {
            pointerEvents: 'none',
          },
          ...(help && {
            border: '2px solid !important',
          }),
          ...(isRawColor &&
            !!color && {
              backgroundColor: color,
              '&:hover': {
                backgroundColor: theme.darken(color, 0.04),
              },
              '&:active': {
                backgroundColor: `${theme.darken(color, 0.08)} !important`,
              },
              '&:focus': {
                boxShadow: `0 0 0 0.25rem ${theme.rgba(color, 0.4)} !important`,
              },
            }),
        })}
        {...((link != null || to != null) &&
          (Tag === 'a'
            ? {
                href: to ?? link,
                target: target ?? (openLinkInNewTab ? '_blank' : '_self'),
                download,
              }
            : { to: to ?? link }))}
        onMouseEnter={() => {
          setHovering(true);
        }}
        onMouseLeave={() => {
          setHovering(false);
        }}
        {...componentProps}
      >
        {!!swatch && (
          <div
            className="rounded-pill pe-none position-absolute"
            css={{
              bottom: '0.125rem',
              left: '0.25rem',
              right: '0.25rem',
              height: '0.125rem',
              backgroundColor:
                colorContrast(swatch, '#fff') < 1.3 ? darkenBy(swatch, 0.1).toString() : swatch,
            }}
          />
        )}
        {!hasCaret ? (
          btnContent
        ) : (
          <>
            {hasContent && (
              <span
                className={cx('hstack gap-1 mw-100', {
                  'text-truncate': truncate,
                  'w-100': fullWidth,
                })}
              >
                {btnContent}
              </span>
            )}
            <Icon
              name="chevron-down"
              size="xs"
              fixedWidth
              className="btn-caret flex-shrink-0"
              css={{
                '.dropend &': { transform: 'rotate(-90deg)' },
                '.dropstart &': { display: 'none' },
              }}
            />
          </>
        )}
        {beta && (
          <ODSInfo
            message={<FormattedMessage {...messages.beta} />}
            className="d-flex align-items-center justify-content-center rounded-circle bg-secondary position-absolute"
            color="white"
            css={{
              width: '1rem',
              height: '1rem',
              padding: '0.275em',
              svg: { width: '100%', height: '100%' },
              top: 0,
              right: 0,
              transform: 'translate(50%, -50%)',
              pointerEvents: 'all',
            }}
            placement="right"
            tooltip
          >
            <Icon name="flask" className="opacity-75" />
          </ODSInfo>
        )}
      </Tag>
    );

    if (!tooltip && !shortcut) {
      return btn;
    }

    return (
      <ODSTooltip
        ref={ref}
        message={tooltip ?? shortcut}
        arrow={tooltipArrow}
        placement={tooltipPlacement}
        offset={tooltipOffset}
        shortcut={tooltip ? shortcut : undefined}
        ignoreOverflow
        className="text-center"
      >
        {btn}
      </ODSTooltip>
    );
  }
);

export type { Props as ODSButtonProps };
export default ODSButton;
